import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton, SecondaryButton, DangerButton } from "./Buttons.jsx";
import ModalBasic from "./ModalBasic.jsx";
import {
  FaFingerprint,
  FaTrash,
  FaPlus,
  FaMobile,
  FaDesktop,
} from "react-icons/fa6";
import { addToast, checkSession } from "../store/app/action.js";
import { deleteWebAuthnCredential } from "../api/auth.js";

function WebAuthnManageModal({ isOpen, onClose, onAddCredential }) {
  const { user } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [deletingCredential, setDeletingCredential] = useState(null);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);

  const handleClose = () => {
    setDeletingCredential(null);
    setConfirmDeleteModalOpen(false);
    setCredentialToDelete(null);
    onClose();
  };

  const handleDeleteCredential = (credential) => {
    setCredentialToDelete(credential);
    setConfirmDeleteModalOpen(true);
  };

  const confirmDeleteCredential = async () => {
    if (!credentialToDelete) return;

    setDeletingCredential(credentialToDelete.internal_id);
    setConfirmDeleteModalOpen(false);

    try {
      const response = await deleteWebAuthnCredential(
        credentialToDelete.internal_id
      );

      if (response.status === "success") {
        dispatch(
          addToast({
            type: "success",
            message: "Passkey deleted successfully",
            timer: 3,
          })
        );

        // Refresh user data to get updated MFA status
        dispatch(checkSession());
      } else {
        throw new Error(response.message || "Failed to delete passkey");
      }
    } catch (error) {
      dispatch(
        addToast({
          type: "error",
          message:
            error.message || "Failed to delete passkey. Please try again.",
          timer: 5,
        })
      );
    } finally {
      setDeletingCredential(null);
      setCredentialToDelete(null);
    }
  };

  const getDeviceIcon = (credential) => {
    // Simple heuristic based on credential name or other properties
    const name = credential.name?.toLowerCase() || "";
    if (
      name.includes("iphone") ||
      name.includes("android") ||
      name.includes("mobile")
    ) {
      return <FaMobile className="w-4 h-4" />;
    }
    return <FaDesktop className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const credentials = user?.mfa?.webauthn_credentials || [];

  return (
    <>
      <ModalBasic
        id="webauthn-manage-modal"
        modalOpen={isOpen}
        setModalOpen={handleClose}
        title="Manage Passkeys"
        width="2xl"
      >
        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/50 mx-auto mb-4">
              <FaFingerprint className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 text-center mb-2">
              Manage Your Passkeys
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-center">
              View and manage your registered passkeys. You can add new ones or
              remove existing ones.
            </p>
          </div>

          {/* Add New Passkey Button */}
          <div className="mb-6">
            <PrimaryButton
              width="full"
              onClick={() => {
                handleClose();
                if (onAddCredential) {
                  onAddCredential();
                }
              }}
            >
              <div className="flex items-center">
                <FaPlus className="w-4 h-4 mr-2" />
                Add New Passkey
              </div>
            </PrimaryButton>
          </div>

          {/* Existing Credentials */}
          <div className="space-y-4">
            <h3 className="font-medium text-slate-900 dark:text-slate-100">
              Registered Passkeys ({credentials.length})
            </h3>

            {credentials.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <FaFingerprint className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No passkeys registered yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {credentials.map((credential, index) => (
                  <div
                    key={index}
                    className="border border-slate-200 dark:border-slate-700 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                          {getDeviceIcon(credential)}
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {credential.name || `Passkey ${index + 1}`}
                          </h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Created {formatDate(credential.created_at)}
                          </p>
                          {credential.last_used && (
                            <p className="text-xs text-slate-400 dark:text-slate-500">
                              Last used {formatDate(credential.last_used)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <DangerButton
                          onClick={() => handleDeleteCredential(credential)}
                          disabled={
                            deletingCredential === credential.internal_id
                          }
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <FaTrash className="w-3 h-3" />
                        </DangerButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <SecondaryButton onClick={handleClose}>Close</SecondaryButton>
          </div>
        </div>
      </ModalBasic>

      {/* Delete Confirmation Modal */}
      <ModalBasic
        id="confirm-delete-webauthn-credential-modal"
        modalOpen={confirmDeleteModalOpen}
        setModalOpen={setConfirmDeleteModalOpen}
        title="Delete Passkey"
        width="lg"
      >
        <div className="px-6 py-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                <FaTrash className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                Delete "{credentialToDelete?.name || "Passkey"}"?
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                This will permanently delete this passkey. You'll need to set up
                a new one if you want to use passkey authentication again.
              </p>
              <div className="flex justify-end space-x-3">
                <SecondaryButton
                  onClick={() => setConfirmDeleteModalOpen(false)}
                  disabled={deletingCredential}
                >
                  Cancel
                </SecondaryButton>
                <DangerButton
                  onClick={confirmDeleteCredential}
                  disabled={deletingCredential}
                >
                  {deletingCredential ? "Deleting..." : "Delete"}
                </DangerButton>
              </div>
            </div>
          </div>
        </div>
      </ModalBasic>
    </>
  );
}

export default WebAuthnManageModal;
